import { useMemo } from "react";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  LineChart as LineChartIcon,
  Clock,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { getHistory } from "../../lib/history";
import { fmtCurrency, fmtPct, fmtNumber } from "../../lib/format";
/**
 * Period-over-period "Welcome back" delta callout.
 *
 * When a user runs a second (or third, or fourth) report, this card
 * appears at the top of the Insights section and highlights how they've
 * moved vs their most recent prior submission — letter grade change,
 * days between reports, and a table of 6 core metrics with deltas.
 *
 * Only renders when there IS a prior period in local history, so
 * first-time visitors don't see an empty welcome-back card.
 */
export default function PeriodDeltaCard({ report }) {
  const delta = useMemo(() => {
    if (!report) return null;
    const history = getHistory();
    if (!history || history.length < 1) return null;
    // Find the most recent saved period that is NOT the current report.
    // We tolerate legacy entries (which lack `health`/`input` objects) by
    // treating them as missing when their nested fields are absent.
    const prior = history.find(
      (h) => h.id !== report.id && h.metrics
    );
    if (!prior) return null;
    return computeDelta(prior, report);
  }, [report]);

  if (!delta) return null;

  return (
    <div className="mb-6" data-testid="period-delta-card">
      <div className="bg-white border border-[#E5E0D8] rounded-2xl overflow-hidden">
        {/* -- WELCOME BACK HERO ---------------------------------------- */}
        <div
          className="bg-[#1F382E] text-[#F7F5F0] px-6 sm:px-8 py-6 sm:py-7"
          data-testid="period-delta-hero"
        >
          <div className="flex items-center gap-2.5">
            <LineChartIcon
              size={14}
              strokeWidth={1.75}
              className="text-[#C26D5C]"
            />
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#C26D5C] font-medium">
              Welcome back
            </p>
          </div>
          <p
            className="font-serif-display text-2xl sm:text-3xl tracking-tight mt-2 leading-tight max-w-2xl"
            data-testid="period-delta-headline"
          >
            {delta.headline}
          </p>

          {/* Enterprise value delta — the emotional hero. Shown above the
              letter-grade transition when a prior valuation exists. */}
          {delta.valuationTransition && (
            <div
              className="mt-5 flex items-baseline gap-3 flex-wrap"
              data-testid="period-delta-valuation-transition"
            >
              <span className="text-[#F7F5F0]/60 text-[13px] uppercase tracking-[0.18em]">
                Enterprise value
              </span>
              <span className="font-serif-display text-2xl sm:text-3xl text-[#F7F5F0]/50 line-through decoration-[#C26D5C]/60 decoration-2 tabular-nums">
                {delta.valuationTransition.priorMidLabel}
              </span>
              <ArrowRight
                size={18}
                strokeWidth={1.5}
                className="text-[#C26D5C]"
              />
              <span
                className="font-serif-display text-3xl sm:text-4xl text-[#F7F5F0] tabular-nums"
                data-testid="period-delta-valuation-now"
              >
                {delta.valuationTransition.currentMidLabel}
              </span>
              <span
                className={`text-sm font-medium ${delta.valuationTransition.tone === "good" ? "text-[#A8D8B9]" : delta.valuationTransition.tone === "bad" ? "text-[#F4B0A0]" : "text-[#F7F5F0]/60"}`}
              >
                {delta.valuationTransition.deltaText}
              </span>
            </div>
          )}

          {/* Grade transition — the emotional payoff */}
          {delta.gradeTransition && (
            <div
              className="mt-5 flex items-baseline gap-3 flex-wrap"
              data-testid="period-delta-grade-transition"
            >
              <span className="text-[#F7F5F0]/60 text-[13px] uppercase tracking-[0.18em]">
                CFO Grade
              </span>
              <span className="font-serif-display text-3xl sm:text-4xl text-[#F7F5F0]/50 line-through decoration-[#C26D5C]/60 decoration-2 tabular-nums">
                {delta.gradeTransition.priorRating}
              </span>
              <ArrowRight
                size={18}
                strokeWidth={1.5}
                className="text-[#C26D5C]"
              />
              <span
                className="font-serif-display text-4xl sm:text-5xl text-[#F7F5F0] tabular-nums"
                data-testid="period-delta-grade-now"
              >
                {delta.gradeTransition.currentRating}
              </span>
              <span
                className={`text-sm font-medium ${delta.gradeTransition.tone === "good" ? "text-[#A8D8B9]" : delta.gradeTransition.tone === "bad" ? "text-[#F4B0A0]" : "text-[#F7F5F0]/60"}`}
              >
                {delta.gradeTransition.scoreDeltaText}
              </span>
            </div>
          )}

          {/* 60+ day nudge — only appears if the user has been away for a while */}
          {delta.daysBetween >= 60 && (
            <div
              className="mt-5 inline-flex items-center gap-2 text-[13px] text-[#F7F5F0]/80 bg-[#F7F5F0]/8 rounded-full px-3 py-1.5"
              data-testid="period-delta-nudge"
            >
              <Clock size={12} strokeWidth={1.75} />
              <span>
                It&apos;s been {delta.daysBetween} days since your last
                check-in. Owners who rerun quarterly catch problems earliest.
              </span>
            </div>
          )}
        </div>

        {/* -- METRIC TABLE --------------------------------------------- */}
        <div className="px-6 sm:px-8 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-[#E5E0D8]">
          <p className="text-[13px] text-[#5C665D]">
            <span className="text-[#1F382E] font-medium">
              {delta.daysBetween} days
            </span>{" "}
            between {delta.priorPeriod} and {delta.currentPeriod}
          </p>
          <Link to="/trends" className="shrink-0">
            <Button
              variant="outline"
              className="border-[#1F382E]/20 text-[#1F382E] hover:bg-[#1F382E] hover:text-[#F7F5F0] rounded-md h-9 px-4 transition-all duration-300 text-[13px] whitespace-nowrap"
              data-testid="period-delta-trends-cta"
            >
              See full trends
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 divide-x divide-y sm:divide-y-0 divide-[#E5E0D8]">
          {delta.rows.map((row, i) => (
            <DeltaCell
              key={row.key}
              row={row}
              dataTestid={`delta-${row.key}-${i}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function DeltaCell({ row, dataTestid }) {
  const Icon =
    row.direction === "up"
      ? TrendingUp
      : row.direction === "down"
        ? TrendingDown
        : Minus;
  const color =
    row.tone === "good"
      ? "text-[#2C5C43]"
      : row.tone === "bad"
        ? "text-[#A84632]"
        : "text-[#5C665D]";
  return (
    <div className="p-4 sm:p-5 min-w-0" data-testid={dataTestid}>
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#5C665D] mb-1">
        {row.label}
      </p>
      <p className="font-serif-display text-xl text-[#1F382E] tabular-nums leading-none">
        {row.now}
      </p>
      <p className="text-[11px] text-[#5C665D] mt-1 truncate tabular-nums">
        was {row.prior}
      </p>
      <p
        className={`mt-1.5 text-[12px] flex items-center gap-1 ${color} font-medium tabular-nums`}
      >
        <Icon size={11} strokeWidth={2} />
        {row.deltaText}
      </p>
    </div>
  );
}

function computeDelta(prior, current) {
  const pm = prior.metrics || {};
  const cm = current.metrics || {};
  // Support both new-schema (structured health object) and legacy (flat
  // `score`/`rating` fields on the history entry) prior snapshots.
  const priorHealth = prior.health || {
    overall_score: prior.score ?? null,
    rating: prior.rating ?? null,
  };
  const ch = current.health || {};
  const cur = (current.input || {}).currency || "USD";

  // Days between reports — used for the nudge and the "N days between..." line.
  const priorSavedAt = prior.saved_at ? new Date(prior.saved_at) : null;
  const nowTs = new Date();
  const daysBetween = priorSavedAt
    ? Math.max(1, Math.round((nowTs - priorSavedAt) / (1000 * 60 * 60 * 24)))
    : null;

  // Period labels — prefer the enriched input.period, fall back to the flat
  // top-level `period` used by legacy history entries.
  const priorPeriod =
    (prior.input && prior.input.period) || prior.period || "your last report";
  const currentPeriod = (current.input || {}).period || "this period";

  // Higher-is-better metrics
  const higherBetter = new Set([
    "ebitda",
    "ebitda_margin",
    "gross_margin",
    "net_margin",
    "dscr",
    "current_ratio",
    "free_cash_flow",
    "runway_months",
    "overall_score",
  ]);

  function row(key, label, fmt) {
    const c = key === "overall_score" ? ch.overall_score : cm[key];
    const p =
      key === "overall_score" ? priorHealth.overall_score : pm[key];
    if (c == null || p == null) return null;
    const numericDelta = c - p;
    const direction =
      numericDelta > 0.001
        ? "up"
        : numericDelta < -0.001
          ? "down"
          : "flat";
    const isHB = higherBetter.has(key);
    const tone =
      direction === "flat"
        ? "neutral"
        : (direction === "up" && isHB) ||
            (direction === "down" && !isHB)
          ? "good"
          : "bad";
    const deltaText =
      direction === "flat"
        ? "no change"
        : `${numericDelta > 0 ? "+" : ""}${fmt(numericDelta, { signed: true })}`;
    return {
      key,
      label,
      now: fmt(c),
      prior: fmt(p),
      deltaText,
      direction,
      tone,
    };
  }

  const moneyFmt = (v) => fmtCurrency(v, cur);
  const pctFmt = (v) => fmtPct(v);
  const numFmt = (v) => fmtNumber(v);
  const scoreFmt = (v) => (v == null ? "—" : `${Math.round(v)}`);

  const rows = [
    row("overall_score", "CFO Grade", scoreFmt),
    row("ebitda", "EBITDA", moneyFmt),
    row("ebitda_margin", "EBITDA margin", pctFmt),
    row("dscr", "DSCR", numFmt),
    row("current_ratio", "Current ratio", numFmt),
    row("free_cash_flow", "Free cash flow", moneyFmt),
  ].filter(Boolean);

  if (rows.length === 0) return null;

  // Letter-grade transition — the emotional hook. Only render when both
  // ratings exist and are legitimate letter grades.
  let gradeTransition = null;
  if (
    priorHealth.rating &&
    ch.rating &&
    priorHealth.rating !== "—" &&
    ch.rating !== "—"
  ) {
    const scoreDelta =
      (ch.overall_score ?? 0) - (priorHealth.overall_score ?? 0);
    const tone =
      Math.abs(scoreDelta) < 0.5
        ? "neutral"
        : scoreDelta > 0
          ? "good"
          : "bad";
    gradeTransition = {
      priorRating: priorHealth.rating,
      currentRating: ch.rating,
      scoreDelta,
      scoreDeltaText:
        tone === "neutral"
          ? "held steady"
          : `${scoreDelta > 0 ? "+" : ""}${Math.round(scoreDelta)} pts`,
      tone,
    };
  }

  // Enterprise-value transition — the dollar-change hero, the strongest
  // possible retention hook. Computed from the persisted prior valuation
  // and the current report.valuation_range. Skipped if either is missing
  // (e.g. first-ever period, or negative-EBITDA period).
  let valuationTransition = null;
  const priorVal = prior.valuation;
  const curVal = current.valuation_range;
  if (
    priorVal &&
    curVal?.available &&
    priorVal.mid != null &&
    curVal.buyer_view?.mid != null
  ) {
    const priorMid = priorVal.mid;
    const curMid = curVal.buyer_view.mid;
    const dollarDelta = curMid - priorMid;
    const pctDelta = priorMid > 0 ? dollarDelta / priorMid : 0;
    // Only surface if the change is materially large — > 2% of prior — so
    // small quarter-to-quarter noise doesn't dominate the hero.
    const material = Math.abs(pctDelta) >= 0.02;
    const tone = !material
      ? "neutral"
      : dollarDelta > 0
        ? "good"
        : "bad";
    const cur = priorVal.currency || curVal.currency || "USD";
    valuationTransition = {
      priorMid,
      currentMid: curMid,
      priorMidLabel: fmtCurrency(priorMid, cur),
      currentMidLabel: fmtCurrency(curMid, cur),
      dollarDelta,
      pctDelta,
      deltaText: !material
        ? "roughly unchanged"
        : `${dollarDelta > 0 ? "+" : ""}${fmtCurrency(dollarDelta, cur)} · ${(pctDelta * 100).toFixed(0)}%`,
      tone,
      material,
      currency: cur,
    };
  }

  // Headline copy — dollar changes lead when material, otherwise fall back
  // to grade-based framing.
  let headline;
  if (valuationTransition && valuationTransition.material) {
    if (valuationTransition.tone === "good") {
      headline = `You built ${fmtCurrency(valuationTransition.dollarDelta, valuationTransition.currency)} of enterprise value since ${priorPeriod}.`;
    } else {
      headline = `You lost ${fmtCurrency(Math.abs(valuationTransition.dollarDelta), valuationTransition.currency)} of enterprise value this period — here's why.`;
    }
  } else if (gradeTransition && gradeTransition.tone === "good") {
    headline = `You've moved up since ${priorPeriod}.`;
  } else if (gradeTransition && gradeTransition.tone === "bad") {
    headline = `Some things slipped since ${priorPeriod} — here's what.`;
  } else {
    headline = `Here's how you've changed since ${priorPeriod}.`;
  }

  return {
    priorPeriod,
    currentPeriod,
    daysBetween: daysBetween ?? 0,
    headline,
    valuationTransition,
    gradeTransition,
    rows,
  };
}
